import React, { createContext, useState, useEffect, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { serverGet } from "../../middleware/http";
import { handoffGet } from "../utils/ssoHandoff";
import { apiConst } from "../constant/constant";
import { isArray } from "../utils/validation";

const ManageResourceontext = createContext(null);

export const useManageResource = () => {
  return useContext(ManageResourceontext);
};

export const ResourceProvider = ({ children }) => {
  const [resourceList, setResourceList] = useState(null);
  const [portalResourceContent, setPortalResourceContent] = useState(null);
  const [error, setError] = useState(false);

  const location = useLocation();

  // Same pattern as AccSettingContext.js's bootstrap fields: getResourceBootstrap() (below)
  // claims both flags synchronously so a concurrent getResource()/getPortalResourceContent()
  // call -- from the boot effect below, or getCurrentResource()/getCurrentResourceAction()'s own
  // fallback fetch -- awaits the same in-flight request instead of firing a second, identical one.
  const fetchingRef = useRef({ resource: false, portalContent: false });
  const resourcePromiseRef = useRef(null);
  const portalContentPromiseRef = useRef(null);

  const applyResourceResponse = (data) => {
    setResourceList(data);
  };

  const applyPortalContentResponse = (data) => {
    setPortalResourceContent(data);
  };

  const getResource = async () => {
    try {
      if (resourceList) return resourceList;

      if (fetchingRef.current.resource) {
        return await resourcePromiseRef.current;
      }

      fetchingRef.current.resource = true;
      resourcePromiseRef.current = (async () => {
        try {
          const res = await serverGet(`${apiConst.getResource}`);
          if (res?.data !== null && res?.status == 1) {
            applyResourceResponse(res?.data);
            return res?.data;
          } else {
            setError(true);
          }
        } catch (e) {
          console.log(e);
        } finally {
          fetchingRef.current.resource = false;
        }
      })();
      return await resourcePromiseRef.current;
    } catch (e) {
      console.log(e);
    }
  };

  const getPortalResourceContent = async () => {
    try {
      if (portalResourceContent) return portalResourceContent;

      if (fetchingRef.current.portalContent) {
        return await portalContentPromiseRef.current;
      }

      fetchingRef.current.portalContent = true;
      portalContentPromiseRef.current = (async () => {
        try {
          const res = await serverGet(apiConst.getPortalContent);
          if (res?.data && res?.status == 1) {
            applyPortalContentResponse(res?.data);
            return res?.data;
          }
        } catch (error) {
          console.error(error);
        } finally {
          fetchingRef.current.portalContent = false;
        }
      })();
      return await portalContentPromiseRef.current;
    } catch (error) {
      console.error(error);
    }
  };

  // Combines getResource() + getPortalResourceContent() into one round trip
  // (account/resourcebootstrap) for the boot effect below. No caching layer -- only replaces WHO
  // fires the first request for each field; getResource()/getPortalResourceContent() themselves
  // are unchanged for every other caller (getCurrentResource, getCurrentResourceAction), which
  // either see the field already resolved or await this same in-flight request.
  //
  // Per-field error retry: if either field comes back with its own error marker (or the whole
  // call fails), only THAT field is retried via its own individual endpoint afterward -- not the
  // whole aggregate again.
  const getResourceBootstrap = async () => {
    try {
      if (fetchingRef.current.resource || fetchingRef.current.portalContent) {
        return;
      }

      fetchingRef.current.resource = true;
      fetchingRef.current.portalContent = true;

      const bootstrapPromise = (async () => {
        try {
          // handoffGet: right after an SSO entry this was already fetched (post sign-in) while the
          // SSO loader was showing -- see ssoHandoff.js. Otherwise a plain fetch.
          const res = await handoffGet(apiConst.getResourceBootstrap);
          const data = res?.status == 1 ? res?.data : null;

          const resourceResolved = data != null && !data?.resourceError && data?.resource != null;
          if (resourceResolved) {
            applyResourceResponse(data.resource);
          } else if (data != null && data?.resourceError) {
            // Matches getResource()'s own error behavior (its else-branch sets `error`) --
            // only for a genuine error marker, not for "the whole call failed" (data == null),
            // which the retry block below already covers via its own getResource() fallback.
            setError(true);
          }

          // != null (not isArray, which requires non-empty) -- matches getPortalResourceContent()'s
          // own success check (`res?.data && res?.status == 1`), which accepts a legitimately empty
          // content list. isArray() here would wrongly flag an empty-but-successful list as errored,
          // triggering a needless retry every boot for a portal with no content records.
          const portalContentResolved = data != null && !data?.portalContentError && data?.portalContent != null;
          if (portalContentResolved) applyPortalContentResponse(data.portalContent);

          return {
            resourceData: resourceResolved ? data.resource : null,
            portalContentData: portalContentResolved ? data.portalContent : null,
            errors: {
              resource: !resourceResolved,
              portalContent: !portalContentResolved,
            },
          };
        } catch (error) {
          console.error(error);
          return null;
        } finally {
          fetchingRef.current.resource = false;
          fetchingRef.current.portalContent = false;
        }
      })();

      resourcePromiseRef.current = bootstrapPromise.then((r) => r?.resourceData);
      portalContentPromiseRef.current = bootstrapPromise.then((r) => r?.portalContentData);

      const result = await bootstrapPromise;

      // Fire-and-forget: each retried getX() manages its own fetchingRef/promiseRef/state
      // independently (the flags above are already cleared by this point).
      if (result?.errors?.resource) getResource();
      if (result?.errors?.portalContent) getPortalResourceContent();
    } catch (error) {
      console.error(error);
    }
  };

  const getCurrentResource = (id, pcode, arr) => {
    if (isArray(resourceList?.resources) || isArray(arr)) {
      let data = "";
      if (isArray(arr)) {
        data = [...arr];
      } else if (isArray(resourceList?.resources)) {
        data = [...resourceList?.resources];
      }
      const _parentId = data?.find((list) => list?.code == pcode);

      const _currentRes = data?.filter((list) =>
        pcode
          ? list?.parentId == _parentId?.id && list.typeId == id
          : list.typeId == id
      );
      return _currentRes;
    } else {
      if (!error) getResource();
    }
  };

  const getCurrentResourceAction = (code, pCode, arr, act) => {
    if (
      (Array.isArray(resourceList?.resources) &&
        Array.isArray(resourceList?.actions)) ||
      (isArray(arr) && isArray(act))
    ) {
      let data = "";
      if (isArray(arr)) {
        data = [...arr];
      } else if (isArray(resourceList?.resources)) {
        data = [...resourceList?.resources];
      }

      let _actions = "";
      if (isArray(act)) {
        _actions = [...act];
      } else if (isArray(resourceList?.resources)) {
        _actions = [...resourceList?.actions];
      }

      let parentId = "";

      if (pCode)
        parentId = data.find(
          (list) => list?.code == code || list?.parentId == pCode
        )?.parentId;

      const _resource = data.find((list) =>
        parentId
          ? list?.parentId == parentId && list?.code == code
          : list?.code == code
      );
      if (_resource) {
        const _action = _actions?.filter(
          (list) => list?.resourceId == _resource?.id
        );
        if (_action?.length > 0) {
          return _action;
        }
      }
    } else {
      if (!error) getResource();
    }
  };

  const actionExists = (arr, actioncode, feautureCode) => {
    if (Array.isArray(arr)) {
      const actionArray = arr?.filter((list) => list?.code == actioncode);
      if (
        feautureCode &&
        Array.isArray(actionArray) &&
        actionArray?.length > 0
      ) {
        const _featureCode = actionArray[0]?.featureCodes?.split("~");
        return _featureCode?.includes(feautureCode);
      } else {
        return arr?.some((list) => list?.code == actioncode);
      }
    }
  };

  useEffect(() => {
    if (!resourceList && !portalResourceContent) {
      getResourceBootstrap();
    } else {
      if (!resourceList) getResource();
      if (!portalResourceContent) getPortalResourceContent();
    }
  }, [location]);

  return (
    <ManageResourceontext.Provider
      value={{
        resourceList,
        getCurrentResource,
        getCurrentResourceAction,
        actionExists,
        setResourceList,
        portalResourceContent,
        resourceError: error,
      }}
    >
      {children}
    </ManageResourceontext.Provider>
  );
};
